import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ReactFlowProvider, useReactFlow, type Connection } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { ArrowLeft, Eye, Image as ImageIcon, Network, PackagePlus, Save, SquareStack } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { useToast } from '@/hooks/use-toast'
import { handleApiError } from '@/lib/api/axios'
import { classificationsApi, type Classification } from '@/features/classifications/api'
import CoreCanvasUntyped from '@/features/visualization/_legacy/components/CoreCanvas'
import BandRowNode from '@/features/visualization/_legacy/components/BandRowNode'
import { templatesApi, type Template } from '../api'
import { CanvasMenu, type CanvasMenuState, type CanvasMenuTarget } from '../components/CanvasMenu'
import { CategorySelect } from '../components/CategorySelect'
import { ClassificationPalette, CLASSIFICATION_DND_TYPE } from '../components/ClassificationPalette'
import { ObjectEditorDialog } from '../components/ObjectEditorDialog'
import { useTemplateAccess } from '../hooks/useTemplateAccess'
import {
    CARD_NODE_H,
    TemplateObjectNode,
    graphicNodeHeight,
    graphicNodeWidth,
    type ObjectGraphic,
    type TemplateNodeMode,
} from '../components/TemplateObjectNode'
import {
    bandNodes,
    canDepend,
    edgeFromConnection,
    fromTemplate,
    graphEdges,
    minLaneHeight,
    namePatternFor,
    OBJECT_WIDTH,
    objectKeyFor,
    objectNodes,
    ruleSet,
    toSaveInput,
    validateDraft,
    type EdgeDraft,
    type ObjectDraft,
} from '../lib/template-graph'

/**
 * The planning canvas's React Flow surface is untyped JSX, so TypeScript reads every prop its
 * signature destructures as required. This canvas passes the handful it needs.
 */
const CoreCanvas = CoreCanvasUntyped as unknown as React.ComponentType<Record<string, unknown>>

/** See onEdgeClick on the read-only canvas. */
const ignoreEdgeClick = () => {}

/**
 * Fits the drawing into view once it is complete: the template, its classifications (which are
 * what draw the bands) and, in Graphic mode, its graphics. Then it leaves the view alone, so a zoom
 * or pan the user makes stays put while they edit. It fits again for a new template or a new node
 * mode, which change the drawing wholesale.
 *
 * The canvas used to rely on React Flow's fit on first load alone, which fits whatever has arrived
 * by then. Those answers arrive separately. Fitted before the classifications, the view was sized
 * for objects with no bands, and the bands then ran off the edge or sat small in a corner: "The
 * classification background is not scaling with the visualisation in templates" (Andrew,
 * 2026-09-22). The planning canvas waits for its graph to load before it fits, and so does this.
 *
 * React Flow holds a fitView until the nodes it has been given are measured, so calling it the
 * moment the data is complete is safe.
 */
function FitWhenComplete({ fitKey }: { fitKey: string | null }) {
    const { fitView } = useReactFlow()
    const fitted = useRef<string | null>(null)
    useEffect(() => {
        if (!fitKey || fitted.current === fitKey) return
        fitted.current = fitKey
        void fitView({ padding: 0.1, duration: 0 })
    }, [fitKey, fitView])
    return null
}

/**
 * Drawing a template on the same canvas the estate is planned on — the same React Flow surface,
 * the same classification bands, the same edge drawing — with one difference that changes
 * everything: there are no real objects here. Every node is a template object, a KIND of object, so
 * the palette offers classifications, and the band an object lands in is decided by its
 * classification rather than by where it was dropped.
 *
 * Objects and dependencies are changed the way the planning canvas changes them: double-click an
 * object to open it, right-click an object or a dependency for its menu (CanvasMenu).
 *
 * Reached only from the Templates screen (New / Edit / Preview), never from the planning canvas: a
 * planner has no business editing the library by mis-clicking a toggle, and this route carries the
 * templates.* permission guard.
 *
 * Preview is this same page at /templates/:id, READ-ONLY — the template on the canvas it is drawn
 * on, rather than the list of objects the Templates screen used to show in a dialog. Read-only is ONE
 * switch, not a scatter of checks: always on the preview route, and on the edit route whenever
 * this person may not change this template (a shipped one, no templates.update, permissions not
 * known yet, or a template that is not there). When it is on, nothing on the page can change the
 * template: the fields are disabled, Save and the palette are gone, and the canvas neither
 * connects, accepts a drop, nor opens a dialog or a menu on an object or a dependency. What stays
 * is how the template is LOOKED at — zoom, pan, selecting an object, the node mode and the gap
 * sliders — none of it saved.
 *
 * The layout is drawn fresh from the bands on every render. A template has no geometry worth
 * keeping — the instantiate wizard lays the objects out by band when it stamps them — so there is
 * nothing to save and nothing to go stale.
 */
export function TemplateEditorPage({ mode = 'edit' }: { mode?: 'edit' | 'preview' }) {
    const { id } = useParams<{ id?: string }>()
    const isNew = !id || id === 'new'
    const templateId = isNew ? 0 : Number(id)
    const { t } = useTranslation()
    const { toast } = useToast()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const access = useTemplateAccess()

    const [name, setName] = useState('')
    const [category, setCategory] = useState('')
    const [description, setDescription] = useState('')
    // Shipped with the product. Offered, and sent, only where template authoring is on.
    const [shipped, setShipped] = useState(false)
    const [objects, setObjects] = useState<ObjectDraft[]>([])
    const [edges, setEdges] = useState<EdgeDraft[]>([])
    const [loadedId, setLoadedId] = useState<number | null>(null)
    const [selectedKey, setSelectedKey] = useState<string | null>(null)
    const [editingObject, setEditingObject] = useState<string | null>(null)
    // The right-click menu: what it was opened on and where. Kept after it closes, so it can
    // animate out showing what it showed.
    const [menu, setMenu] = useState<CanvasMenuState | null>(null)

    // Canvas controls. A template's geometry is redrawn from the bands on every render and never
    // saved, so these are a per-person way of looking at it — remembered locally, like the
    // planning canvas remembers its own view switches, and belonging to no template in
    // particular.
    const [nodeMode, setNodeMode] = useState<TemplateNodeMode>(() => {
        try { return localStorage.getItem('vs.templates.nodeMode') === 'Graphic' ? 'Graphic' : 'Card' } catch { return 'Card' }
    })
    const [laneHeight, setLaneHeight] = useState(120)
    const [gapX, setGapX] = useState(40)
    useEffect(() => {
        try { localStorage.setItem('vs.templates.nodeMode', nodeMode) } catch { /* private mode */ }
    }, [nodeMode])

    const { data: classifications = [], isSuccess: classificationsLoaded } = useQuery({
        queryKey: ['classifications', 'template-editor'],
        queryFn: async () => (await classificationsApi.list()).data.data,
        staleTime: 5 * 60 * 1000,
    })
    // Every classification's graphics in one call, the same read the classification list screen
    // makes. Only the defaults are used: each is what an object draws until the choice below has
    // answered for it, which is on first opening Graphic mode and for an object just dropped.
    //
    // No `= []` default. In Card mode this query never runs, and a fresh array on every render
    // rebuilt the layout and so every node object. React Flow re-measures a node it is handed anew
    // and hides it until it has, so each keystroke in the fields blanked the canvas for a frame.
    const { data: graphics } = useQuery({
        queryKey: ['classification-graphics'],
        queryFn: async () => (await classificationsApi.graphics()).data.data,
        staleTime: 5 * 60 * 1000,
        enabled: nodeMode === 'Graphic',
    })
    const { data: rules = [] } = useQuery({
        queryKey: ['classification-rules'],
        queryFn: async () => (await classificationsApi.rules()).data.data,
        staleTime: 5 * 60 * 1000,
    })
    // The list is the only read of a single template the API offers, and it is small. Fetched for
    // a new template too: it is also where the category dropdown's options come from.
    const { data: existing, isLoading, isPending, isError: listFailed } = useQuery({
        queryKey: ['templates', 'admin'],
        queryFn: async () => ((await templatesApi.adminList()).data as { data: Template[] }).data,
    })

    const template = useMemo(
        () => (isNew ? null : (existing || []).find((tpl) => Number(tpl.id) === templateId) || null),
        [existing, isNew, templateId],
    )

    const isSystem = !!template?.IsSystem
    // The list has answered and this template is not in it — deleted since the link was made, or
    // never there. There is nothing to save over, so nothing is offered.
    const missing = !isNew && !isPending && !template
    // Whether this person may change this template here: templates.update, and for a shipped
    // template, an environment where template authoring is on. Not known yet counts as "not yet":
    // never as a yes, and never guessed from the role name.
    const canChange = !isNew && !missing && access.canChange(template)
    // A new template has nothing to protect; /templates/new is never read-only.
    const readOnly = !isNew && (mode === 'preview' || !canChange)
    // On the edit route the only thing missing may be the answer: say so, rather than label the
    // page a preview for the moment before it becomes an editor.
    const checking = readOnly && mode === 'edit' && !missing && access.status === 'checking'
    // Why it is read-only, where there is more to say than "preview".
    const readOnlyNote = missing
        ? (listFailed
            ? t('common.errorLoadingData', 'Error loading data')
            : t('templates.editor.notFound', 'This template could not be found. It may have been deleted.'))
        : isSystem && access.status === 'ready' && !access.authoring
            // A shipped template is VaultSentinel's, changed only where authoring is on. The
            // server refuses any change here.
            ? t('templates.editor.systemReadOnly', 'This template ships with the product. It can be previewed but not restructured.')
            : access.status === 'failed'
                ? t('templates.permissionsUnavailable', 'Your permissions could not be loaded, so templates open read-only. Reload the page to try again.')
                : access.status === 'ready' && !access.canEdit
                    ? t('templates.editor.noPermission', 'You do not have permission to change templates.')
                    : null

    // Seed the editor once the template arrives, and never again: after that the drawing belongs
    // to the user, and a background refetch must not overwrite work in progress.
    useEffect(() => {
        if (!template || loadedId === templateId) return
        const opened = fromTemplate(template)
        setLoadedId(templateId)
        setName(template.Name)
        setCategory(template.Category || '')
        setDescription(template.Description || '')
        setShipped(!!template.IsSystem)
        setObjects(opened.objects)
        setEdges(opened.edges)
    }, [template, loadedId, templateId])

    const classById = useMemo(
        () => new Map<number, Classification>(classifications.map((c) => [Number(c.id), c])),
        [classifications],
    )
    const ruleIndex = useMemo(() => ruleSet(rules), [rules])
    const drawnClassIds = useMemo(() => objects.map((r) => Number(r.ClassificationID)), [objects])
    const problems = useMemo(
        () => validateDraft(objects, edges, ruleIndex, classById),
        [objects, edges, ruleIndex, classById],
    )

    const defaultGraphicByClass = useMemo(() => {
        const out = new Map<number, ObjectGraphic>()
        for (const g of graphics ?? []) {
            if (g.IsDefault && g.ShapeFilename) {
                out.set(Number(g.ClassificationID), {
                    filename: g.ShapeFilename,
                    width: g.ShapeWidth ?? null,
                    height: g.ShapeHeight ?? null,
                })
            }
        }
        return out
    }, [graphics])

    // What each object will draw on the planning canvas once placed: the classification's graphic
    // whose tags best match the object's description, else its default. The server chooses, with
    // the planning canvas's own matcher (backend services/shape-selection.js), so the two canvases
    // cannot disagree. This used to draw the default whatever the description said: "Hypervisor
    // on Server" drew a bare hypervisor here and a hypervisor on a server once placed (Andrew,
    // 2026-09-22).
    //
    // Keyed on only what the choice reads, so an edit that cannot change a graphic (a count, the
    // attach flags, the template's own fields) asks nothing. The previous answer stays up while a
    // new one is fetched, so a changed description swaps the picture once instead of blanking it.
    const graphicRequest = useMemo(
        () => objects.map((o) => ({
            ObjectKey: o.ObjectKey,
            ClassificationID: Number(o.ClassificationID),
            DefaultDescription: o.DefaultDescription ?? null,
        })),
        [objects],
    )
    const { data: chosenGraphics, isPlaceholderData: graphicsPending } = useQuery({
        queryKey: ['template-object-graphics', graphicRequest],
        queryFn: async () => (await templatesApi.objectGraphics(graphicRequest)).data.data,
        staleTime: 5 * 60 * 1000,
        enabled: nodeMode === 'Graphic' && graphicRequest.length > 0,
        placeholderData: keepPreviousData,
    })
    // Each object's graphic: the choice once it has answered for that object (null: no graphic is
    // assigned to its classification), the classification's default until then.
    const graphicByKey = useMemo(() => {
        const out = new Map<string, ObjectGraphic | null>()
        for (const o of objects) {
            const chosen = chosenGraphics?.[o.ObjectKey]
            out.set(o.ObjectKey, chosen === undefined
                ? defaultGraphicByClass.get(Number(o.ClassificationID)) ?? null
                : chosen && { filename: chosen.filename, width: chosen.width, height: chosen.height })
        }
        return out
    }, [objects, chosenGraphics, defaultGraphicByClass])

    // The footprint every object in this mode needs. Graphics are drawn at their own proportions,
    // so the widest and tallest artwork on the canvas sets the spacing — a fixed guess either
    // overlapped a wide rack unit with its neighbour or let a tall one hang out of its band. The
    // sliders still set the gaps; they just cannot shrink a lane below what its contents need.
    const layout = useMemo(() => {
        const drawn = objects.map((r) => graphicByKey.get(r.ObjectKey) ?? null)
        const graphic = nodeMode === 'Graphic'
        const objectWidth = graphic
            ? Math.max(OBJECT_WIDTH.Graphic, ...drawn.map(graphicNodeWidth))
            : OBJECT_WIDTH.Card
        const nodeHeight = graphic
            ? Math.max(0, ...drawn.map(graphicNodeHeight))
            : CARD_NODE_H
        return { objectWidth, laneHeight: Math.max(laneHeight, minLaneHeight(nodeHeight)), gapX }
    }, [objects, graphicByKey, nodeMode, laneHeight, gapX])

    const nodes = useMemo(() => [
        ...bandNodes(objects, classById, (rank) => `${t('visualization.band', 'Band')} ${rank}`, layout),
        ...objectNodes(objects, classById, layout).map((n) => ({
            ...n,
            selected: n.id === selectedKey,
            data: {
                ...n.data,
                mode: nodeMode,
                graphic: graphicByKey.get(n.data.object.ObjectKey) ?? null,
            },
        })),
    ], [objects, classById, selectedKey, t, layout, nodeMode, graphicByKey])
    // Read-only, an edge offers nothing, so it must not look clickable either. (It still has to be
    // under the pointer: see onEdgeClick on the canvas below.)
    const canvasEdges = useMemo(
        () => graphEdges(edges).map((e) => (readOnly ? { ...e, selectable: false } : e)),
        [edges, readOnly],
    )
    const nodeTypes = useMemo(() => ({ templateObject: TemplateObjectNode, bandRow: BandRowNode }), [])

    /** A classification dropped from the palette becomes an object in that class's band. */
    const onDrop = useCallback((e: React.DragEvent) => {
        const payload = e.dataTransfer?.getData?.(CLASSIFICATION_DND_TYPE)
        if (!payload) return
        try {
            const cls = JSON.parse(payload) as Classification
            setObjects((current) => {
                const ObjectKey = objectKeyFor(cls.Name, current.map((r) => r.ObjectKey))
                return [...current, {
                    ObjectKey,
                    ClassificationID: Number(cls.id),
                    NamePattern: namePatternFor(ObjectKey),
                    DefaultDescription: cls.Name,
                    AttachDown: false,
                    AttachUp: false,
                    MasterRunbookID: null,
                }]
            })
        } catch {
            toast({
                title: t('templates.editor.dropFailed', 'That could not be added as an object'),
                description: t('templates.editor.dropFailedBody', 'Drag a classification from the list on the left.'),
                variant: 'destructive',
            })
        }
    }, [t, toast])

    const classOfObject = useCallback(
        (objectKey: string | null | undefined) =>
            Number(objects.find((r) => r.ObjectKey === objectKey)?.ClassificationID ?? 0),
        [objects],
    )

    /**
     * The connection is refused while it is being drawn, rather than at save time: source
     * provides, target depends, and ClassificationRules decides whether that pairing may exist.
     */
    const isValidConnection = useCallback((c: Connection | { source: string | null; target: string | null }) => {
        if (!c.source || !c.target || c.source === c.target) return false
        if (edges.some((e) => e.DependentObject === c.target && e.ProviderObject === c.source)) return false
        return canDepend(ruleIndex, classOfObject(c.target), classOfObject(c.source))
    }, [edges, ruleIndex, classOfObject])

    const onConnect = useCallback((c: Connection) => {
        if (!c.source || !c.target) return
        if (!isValidConnection(c)) {
            const dependent = classById.get(classOfObject(c.target))?.Name || c.target
            const provider = classById.get(classOfObject(c.source))?.Name || c.source
            toast({
                title: t('templates.editor.edgeRefusedTitle', 'Dependency not allowed'),
                description: t(
                    'templates.editor.edgeRefused',
                    '{{dependent}} may not depend on {{provider}} — the classification rules do not allow it.',
                    { dependent, provider },
                ),
                variant: 'destructive',
            })
            return
        }
        setEdges((current) => [...current, edgeFromConnection(c.source!, c.target!)])
    }, [classById, classOfObject, isValidConnection, t, toast])

    /** An object goes with every dependency that names it: an edge to nothing is not an edge. */
    const removeObject = useCallback((objectKey: string) => {
        setObjects((current) => current.filter((r) => r.ObjectKey !== objectKey))
        setEdges((current) => current.filter((e) => e.DependentObject !== objectKey && e.ProviderObject !== objectKey))
        setSelectedKey((key) => (key === objectKey ? null : key))
        setEditingObject((key) => (key === objectKey ? null : key))
    }, [])

    const removeEdge = useCallback((dependent: string, provider: string) => {
        setEdges((current) => current.filter((e) => !(e.DependentObject === dependent && e.ProviderObject === provider)))
    }, [])

    const openMenu = useCallback((event: React.MouseEvent, target: CanvasMenuTarget) => {
        setMenu({ target, x: event.clientX, y: event.clientY, open: true })
    }, [])

    // The menu goes as soon as the canvas pans or zooms, rather than stay pointing at where
    // something was. Unchanged state when nothing is open, so a pan does not re-render the page.
    const closeMenu = useCallback(() => {
        setMenu((current) => (current?.open ? { ...current, open: false } : current))
    }, [])

    // Right-click, as on the planning canvas. The browser's own menu never opens over the drawing,
    // read-only or not; read-only there is simply nothing to offer in its place.
    const onNodeContextMenu = useCallback((event: React.MouseEvent, node: { id: string; type?: string }) => {
        event.preventDefault()
        if (readOnly || node.type !== 'templateObject') return
        openMenu(event, { kind: 'object', objectKey: node.id })
    }, [readOnly, openMenu])

    const onEdgeContextMenu = useCallback((event: React.MouseEvent, edge: { source: string; target: string }) => {
        event.preventDefault()
        if (readOnly) return
        // Source provides, target depends.
        openMenu(event, { kind: 'edge', dependentObject: edge.target, providerObject: edge.source })
    }, [readOnly, openMenu])

    // A menu that was open when the page turned read-only must not reappear if it turns back.
    useEffect(() => {
        if (readOnly) setMenu(null)
    }, [readOnly])

    const saveMutation = useMutation({
        mutationFn: async () => {
            const payload = {
                Name: name.trim(),
                Category: category.trim(),
                Description: description || null,
                // Only where authoring is on: anywhere else the server refuses the field outright.
                ...(access.authoring ? { IsSystem: shipped } : {}),
                ...toSaveInput(objects, edges, classById),
            }
            return isNew ? templatesApi.save(payload) : templatesApi.update(templateId, payload)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['templates'] })
            toast({ title: t('common.success', 'Success'), description: t('templates.saved', 'Template saved.') })
            navigate('/templates')
        },
        onError: (e: unknown) =>
            toast({ title: t('common.error', 'Error'), description: handleApiError(e), variant: 'destructive' }),
    })

    const object = objects.find((r) => r.ObjectKey === editingObject) || null
    const blocked = problems.length > 0 || !name.trim() || saveMutation.isPending
    // Until an existing template has been seeded, the drawing is empty and "needs at least one
    // object" would be a complaint about the loading, not about the template.
    const seeded = isNew || loadedId === templateId
    // When the drawing is complete, and so worth fitting into view (FitWhenComplete).
    const drawingComplete = seeded && classificationsLoaded
        && (nodeMode !== 'Graphic' || !objects.length || (chosenGraphics !== undefined && !graphicsPending))
    const fitKey = drawingComplete ? `${templateId}|${nodeMode}` : null

    // Full height to the window's bottom edge. AppShell wraps every page in `container p-4`, and
    // this used to subtract BOTH paddings — right for a page that scrolls, wrong for a canvas: a
    // dead 1rem strip under the graph, with the canvas controls sitting short of the window edge.
    // Subtract only the top padding and pull the bottom one back with a negative margin, and the
    // sum is still exactly <main>'s height — pt + (H - 1rem) + pb - 1rem = 100vh - topbar — so
    // nothing scrolls and the shell's scroll-feather stays off. The same fix as VisualizationPage,
    // found there first (peer session, 2026-09-21); the two canvases should not differ here.
    return (
        <div
            className="flex flex-col"
            style={{
                width: '100%',
                height: 'calc(100vh - var(--topbar-height, 3.5rem) - 1rem)',
                marginBottom: '-1rem',
                overflow: 'hidden',
            }}
        >
            <div className="flex flex-wrap items-end gap-3 border-b px-4 py-2">
                <Button variant="ghost" size="sm" onClick={() => navigate('/templates')}>
                    <ArrowLeft className="mr-1 h-4 w-4" />
                    {t('templates.editor.back', 'Templates')}
                </Button>
                <div className="space-y-1">
                    <Label htmlFor="tpl-name" className="text-[11px]">{t('templates.name', 'Name')} *</Label>
                    <Input id="tpl-name" className="h-8 w-[220px] text-xs" value={name} maxLength={100}
                           onChange={(e) => setName(e.target.value)} disabled={readOnly} />
                </div>
                <div className="space-y-1">
                    <Label htmlFor="tpl-cat" className="text-[11px]">{t('templates.category', 'Category')}</Label>
                    <CategorySelect
                        id="tpl-cat"
                        className="w-[200px]"
                        value={category}
                        options={(existing || []).map((tpl) => tpl.Category)}
                        onChange={setCategory}
                        disabled={readOnly}
                    />
                </div>
                <div className="space-y-1">
                    <Label htmlFor="tpl-desc" className="text-[11px]">{t('templates.description', 'Description')}</Label>
                    <Input id="tpl-desc" className="h-8 w-[260px] text-xs" value={description} maxLength={1000}
                           onChange={(e) => setDescription(e.target.value)} disabled={readOnly} />
                </div>
                {access.authoring && !readOnly && (
                    <div className="flex items-center gap-2 self-end pb-1.5">
                        <Checkbox id="tpl-shipped" checked={shipped} onCheckedChange={(v) => setShipped(v === true)} />
                        <Label htmlFor="tpl-shipped" className="text-xs" title={t('templates.editor.shippedHint', 'Ships with the product. Customers can preview it but not change it.')}>
                            {t('templates.editor.shippedLabel', 'Shipped template')}
                        </Label>
                    </div>
                )}
                <div className="ml-auto flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                        {t('templates.editor.objectTotal', 'Objects: {{count}}', { count: objects.length })}
                    </span>
                    {!readOnly && (
                        <Button size="sm" onClick={() => saveMutation.mutate()} disabled={blocked}>
                            <Save className="mr-1 h-4 w-4" />
                            {saveMutation.isPending ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
                        </Button>
                    )}
                    {/* Where Save sits when editing, so the way in is where the way out will be. */}
                    {mode === 'preview' && canChange && (
                        <Button size="sm" onClick={() => navigate(`/templates/${templateId}/edit`)}>
                            <Network className="mr-1 h-4 w-4" />
                            {t('templates.editStructure', 'Edit template')}
                        </Button>
                    )}
                </div>
            </div>

            {readOnly && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 border-b bg-muted/40 px-4 py-1.5 text-xs text-muted-foreground">
                    {checking ? t('templates.editor.checkingPermissions', 'Checking your permissions…') : (
                        <>
                            {!missing && (
                                <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                                    <Eye className="h-3.5 w-3.5" />
                                    {t('templates.editor.previewReadOnly', 'Preview — read only')}
                                </span>
                            )}
                            {readOnlyNote && <span>{readOnlyNote}</span>}
                        </>
                    )}
                </div>
            )}

            {/* Editing a shipped template, or making one: this is VaultSentinel's environment, and
                what is drawn here is what customers will see read-only. */}
            {!readOnly && access.authoring && shipped && (
                <p className="border-b border-amber-300 bg-amber-50 px-4 py-1.5 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                    {t('templates.editor.shippedEditing', 'Shipped template. Template authoring is on in this environment, so it can be changed here. Customers see it read-only.')}
                </p>
            )}

            {seeded && problems.length > 0 && (
                <ul className="border-b bg-destructive/5 px-4 py-1.5 text-[11px] text-destructive">
                    {problems.slice(0, 4).map((p, i) => (
                        <li key={`${p.key}-${i}`}>{t(p.key, p.english, { key: p.detail })}</li>
                    ))}
                </ul>
            )}

            <div className="flex min-h-0 flex-1">
                {/* Nothing can be added to a read-only template, so there is nothing to offer —
                    and the canvas takes the width. */}
                {!readOnly && (
                    <aside className="flex w-[260px] min-h-0 shrink-0 flex-col gap-2 border-r p-2">
                        <div>
                            <h2 className="flex items-center gap-1.5 text-xs font-semibold">
                                <PackagePlus className="h-3.5 w-3.5" />
                                {t('templates.editor.paletteHeading', 'Classifications')}
                            </h2>
                            <p className="text-[11px] text-muted-foreground">
                                {t('templates.editor.paletteHint', 'Drag a classification onto the canvas to add an object. Double-click an object to name it.')}
                            </p>
                        </div>
                        <ClassificationPalette
                            classifications={classifications}
                            rules={ruleIndex}
                            drawnClassIds={drawnClassIds}
                        />
                    </aside>
                )}

                <div className="relative min-h-0 flex-1">
                    {/* The planning canvas's own controls panel is not reused here: most of what
                        it offers is about a live estate (colour by scan status, shared services,
                        group by, rebuild, refresh, PDF) and a template has none of that. What
                        does apply is how the objects are drawn and how far apart — and none of it
                        is saved, because the layout is derived from the bands every render. */}
                    <div className="absolute right-3 top-3 z-10 flex items-center gap-3 rounded-lg border bg-background/95 px-2.5 py-1.5 shadow-md backdrop-blur">
                        <div className="flex flex-col gap-0.5">
                            <span className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {t('visualization.controls.nodeMode', 'Node Mode')}
                            </span>
                            <div className="flex gap-1">
                                <button
                                    type="button"
                                    aria-pressed={nodeMode === 'Card'}
                                    title={t('templates.editor.modeCard', 'Cards: the object and its classification')}
                                    className={`rounded border p-1 ${nodeMode === 'Card' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                                    onClick={() => setNodeMode('Card')}
                                >
                                    <SquareStack className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    type="button"
                                    aria-pressed={nodeMode === 'Graphic'}
                                    title={t('templates.editor.modeGraphic', 'Graphics: what each object will draw on the canvas, chosen from its description')}
                                    className={`rounded border p-1 ${nodeMode === 'Graphic' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                                    onClick={() => setNodeMode('Graphic')}
                                >
                                    <ImageIcon className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                        <label className="flex flex-col gap-0.5">
                            <span className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {t('visualization.controls.verticalGap', 'Vertical Gap')}
                            </span>
                            <input
                                type="range" min={80} max={260} step={10} value={laneHeight}
                                onChange={(e) => setLaneHeight(Number(e.target.value))}
                                className="h-3 w-[70px] accent-primary"
                            />
                        </label>
                        <label className="flex flex-col gap-0.5">
                            <span className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {t('visualization.controls.horizontalGap', 'Horizontal Gap')}
                            </span>
                            <input
                                type="range" min={10} max={160} step={10} value={gapX}
                                onChange={(e) => setGapX(Number(e.target.value))}
                                className="h-3 w-[70px] accent-primary"
                            />
                        </label>
                    </div>
                    {/* Keyed on read-only because the palette's column comes and goes with it, and
                        React Flow fits the view only when it mounts: a canvas that fitted itself
                        at full width before the permissions arrived would keep that fit with 260px
                        less room. */}
                    <ReactFlowProvider key={readOnly ? 'read-only' : 'editable'}>
                        <FitWhenComplete fitKey={fitKey} />
                        <CoreCanvas
                            nodes={nodes}
                            edges={canvasEdges}
                            // Geometry is derived from the bands on every render, so there is
                            // nothing for React Flow's own change stream to apply.
                            onNodesChange={() => {}}
                            onEdgesChange={() => {}}
                            nodeTypes={nodeTypes}
                            nodesDraggable={false}
                            nodesConnectable={!readOnly}
                            onConnect={readOnly ? undefined : onConnect}
                            isValidConnection={readOnly ? undefined : isValidConnection}
                            onDrop={readOnly ? undefined : onDrop}
                            onDragOver={(e: React.DragEvent) => { e.dataTransfer.dropEffect = readOnly ? 'none' : 'copy' }}
                            // Selecting stays when read-only: it highlights an object and changes
                            // nothing. It also keeps the objects hoverable — React Flow turns off
                            // pointer events on a node that is neither selectable nor clickable,
                            // and the graphic's classification name lives in its tooltip.
                            onNodeClick={(_e: unknown, node: { id: string; type?: string }) =>
                                setSelectedKey(node.type === 'templateObject' ? node.id : null)}
                            onNodeDoubleClick={readOnly ? undefined : (_e: unknown, node: { id: string; type?: string }) => {
                                if (node.type === 'templateObject') setEditingObject(node.id)
                            }}
                            // An object's and a dependency's menus open on a right-click, as on the
                            // planning canvas. A left click on a dependency opens nothing.
                            onNodeContextMenu={onNodeContextMenu}
                            onEdgeContextMenu={onEdgeContextMenu}
                            onMoveStart={closeMenu}
                            // Read-only edges are not selectable, and React Flow takes an edge that
                            // is neither selectable nor clickable out from under the pointer
                            // (`inactive`, pointer-events: none). A right-click would then reach
                            // the pane behind it and open the browser's menu. A click that does
                            // nothing keeps it hit-testable without making it selectable.
                            onEdgeClick={readOnly ? ignoreEdgeClick : undefined}
                            proOptions={{ hideAttribution: true }}
                        />
                    </ReactFlowProvider>

                    {!readOnly && !objects.length && !isLoading && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                            <p className="max-w-[320px] rounded-lg border bg-background/90 px-4 py-3 text-center text-xs text-muted-foreground">
                                {t('templates.editor.empty', 'Drag a classification from the left to start the template. Its band decides where it lands.')}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Gated here as well as at the double-click: whatever sets the object, a read-only
                page never renders the dialogs that edit one. */}
            {object && !readOnly && (
                <ObjectEditorDialog
                    object={object}
                    classification={classById.get(Number(object.ClassificationID)) || null}
                    takenKeys={objects.map((r) => r.ObjectKey)}
                    onClose={() => setEditingObject(null)}
                    onSave={(next) => {
                        setObjects((current) => current.map((r) => (r.ObjectKey === object.ObjectKey ? next : r)))
                        // Edges reference objects by key, so a rename has to follow through.
                        if (next.ObjectKey !== object.ObjectKey) {
                            setEdges((current) => current.map((e) => ({
                                ...e,
                                DependentObject: e.DependentObject === object.ObjectKey ? next.ObjectKey : e.DependentObject,
                                ProviderObject: e.ProviderObject === object.ObjectKey ? next.ObjectKey : e.ProviderObject,
                            })))
                        }
                        setEditingObject(null)
                    }}
                    onDelete={() => removeObject(object.ObjectKey)}
                />
            )}

            {/* Not rendered read-only, like the object dialog: nothing can open it there. */}
            {!readOnly && (
                <CanvasMenu
                    menu={menu}
                    onOpenChange={(open) => setMenu((current) => (current ? { ...current, open } : current))}
                    onEditObject={setEditingObject}
                    onDeleteObject={removeObject}
                    onRemoveEdge={removeEdge}
                />
            )}
        </div>
    )
}
